'use client';

import Image from 'next/image';
import { esOptimizable } from '@/lib/foto-optimizable';

/**
 * Foto de un vehículo servida por el optimizador de Next.
 *
 * Por qué existe: las fotos se venían sirviendo tal cual desde Supabase
 * Storage, y eso cuesta de dos maneras distintas.
 *
 *  1. **Peso.** Se descarga el archivo original para cualquier tamaño: las
 *     miniaturas de la galería se ven a ~120 px de ancho y bajan el JPEG de
 *     1200×900 completo. Medido en una ficha de Movek: ~790 KB de miniaturas
 *     que por el optimizador son ~60 KB. La foto principal baja de 130 KB a
 *     70 KB en AVIF.
 *  2. **Indexación.** Supabase responde `x-robots-tag: none` en todo objeto
 *     público, así que hoy ninguna foto de la plataforma puede salir en Google
 *     Imágenes. Servida desde el dominio del tenant, ese encabezado no está.
 *
 * El respaldo a `<img>` no es decorativo: `next/image` **lanza** si el host de
 * la URL no está en `remotePatterns`. Hoy las 65.000 fotos viven en Supabase
 * (verificado en la base), pero una importación futura podría traer una URL de
 * otro dominio, y eso dejaría la ficha rota en vez de una foto sin optimizar.
 */

interface Props {
  src: string;
  alt: string;
  /** Se aplica a la imagen, no a un envoltorio: el layout no cambia. */
  className?: string;
  /** Anchos que va a ocupar, para que el optimizador elija la resolución. */
  sizes: string;
  /** Solo para la imagen grande del primer pantallazo (la que mide el LCP). */
  priority?: boolean;
  style?: React.CSSProperties;
  onClick?: () => void;
  draggable?: boolean;
}

export default function FotoOptimizada({
  src,
  alt,
  className,
  sizes,
  priority,
  style,
  onClick,
  draggable,
}: Props) {
  if (!esOptimizable(src)) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        onClick={onClick}
        draggable={draggable}
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : undefined}
      />
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      // `fill` en vez de width/height: el alto y el ancho los pone el
      // contenedor (que ya tiene aspect-ratio), así que no hay salto de layout
      // ni hay que conocer las medidas reales de cada foto.
      fill
      sizes={sizes}
      className={className}
      style={style}
      onClick={onClick}
      draggable={draggable}
      priority={priority}
    />
  );
}
